/** Store → order detail: items, totals, customer, addresses, payment, fulfillments, timeline; fulfill/refund/cancel/note. */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Button, Card, Checkbox, Col, Collapse, Descriptions, Divider, Dropdown, Empty, Form, Input, InputNumber, Modal, Radio, Result, Row, Select, Space, Spin, Table,
    Tag, Timeline, Tooltip, Typography, message,
} from 'antd';
import { ArrowLeftOutlined, MoreOutlined, PrinterOutlined, ReloadOutlined, TagOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { StatusTag, fmtDate, listOf, money, sitePath, toastErr, useSite } from './common';
import { MoneyInput, Thumb, countryLabel } from './ShopCommon';
import { orderName } from './Orders';
import { CarrierSelect, ReturnTag, TrackingTag, openDocument, useCarriers } from './ShopLogistics';
import ShopOrderExtras from './ShopOrderExtras';
import CustomFieldsCard from './CustomFieldsCard';

const { Text, Paragraph } = Typography;

export const Address = ({ a }) => {
    const { t } = useTranslation();
    if (!a || !Object.keys(a).length) return <Text type="secondary">-</Text>;
    return (
        <div style={{ lineHeight: 1.6 }}>
            {a.name && <div>{a.name}</div>}
            {a.company && <div>{a.company}</div>}
            {a.line1 && <div>{a.line1}</div>}
            {a.line2 && <div>{a.line2}</div>}
            <div>{[a.city, a.region, a.postal_code].filter(Boolean).join(', ')}</div>
            {a.country && <div>{countryLabel(a.country, t)}</div>}
            {a.phone && <div><Text type="secondary">{a.phone}</Text></div>}
        </div>
    );
};

const FulfillModal = ({ order, open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const items = useMemo(() => listOf(order?.items).filter((i) => i.requires_shipping !== false && (i.quantity - (i.fulfilled_quantity || 0)) > 0), [order]);
    const [qty, setQty] = useState({});
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const carriers = useCarriers(api);
    useEffect(() => {
        if (open) { setQty(Object.fromEntries(items.map((i) => [i.id, i.quantity - (i.fulfilled_quantity || 0)]))); form.setFieldsValue({ carrier: undefined, tracking_company: '', tracking_number: '', tracking_url: '', notify: true }); }
    }, [open, items, form]);
    const submit = async () => {
        const v = form.getFieldsValue();
        const list = Object.entries(qty).filter(([, n]) => n > 0).map(([id, n]) => ({ order_item_id: Number(id), quantity: n }));
        setBusy(true);
        try {
            await api.post(`/orders/${order.id}/fulfill`, { ...(list.length ? { items: list } : {}), ...v });
            message.success(t('Fulfilled'));
            onDone();
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} onCancel={onClose} title={t('Fulfill items')} onOk={submit} confirmLoading={busy} okText={t('Fulfill')} destroyOnHidden width={600}>
            {items.map((i) => (
                <Row key={i.id} align="middle" gutter={8} style={{ marginBottom: 8 }}>
                    <Col flex="none"><Thumb src={i.image?.src} size={36} /></Col>
                    <Col flex="auto"><div>{i.title}</div><Text type="secondary" style={{ fontSize: 12 }}>{i.variant_title}</Text></Col>
                    <Col flex="110px">
                        <InputNumber min={0} max={i.quantity - (i.fulfilled_quantity || 0)} value={qty[i.id]} onChange={(n) => setQty((q) => ({ ...q, [i.id]: n || 0 }))}
                            addonAfter={`/ ${i.quantity - (i.fulfilled_quantity || 0)}`} />
                    </Col>
                </Row>
            ))}
            <Divider style={{ margin: '12px 0' }} />
            <Form form={form} layout="vertical">
                <Row gutter={12}>
                    <Col xs={24} sm={12}><Form.Item name="carrier" label={t('Shipping carrier')}><CarrierSelect carriers={carriers} /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item name="tracking_number" label={t('Tracking number')}><Input /></Form.Item></Col>
                </Row>
                <Collapse ghost size="small" style={{ marginBottom: 8 }} items={[{
                    key: 'more', label: t('More'),
                    children: (
                        <>
                            <Form.Item name="tracking_company" label={t('Carrier name')}><Input placeholder="UPS, DHL, …" /></Form.Item>
                            <Form.Item name="tracking_url" label={t('Tracking URL')} style={{ marginBottom: 0 }}><Input placeholder="https://" /></Form.Item>
                        </>
                    ),
                }]} />
                <Form.Item name="notify" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox>{t('Send shipping confirmation to the customer')}</Checkbox></Form.Item>
            </Form>
        </Modal>
    );
};

const LabelModal = ({ order, open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const items = useMemo(() => listOf(order?.items).filter((i) => i.requires_shipping !== false && (i.quantity - (i.fulfilled_quantity || 0)) > 0), [order]);
    const [settings, setSettings] = useState(null);
    const [pkg, setPkg] = useState(undefined);
    const [parcel, setParcel] = useState({ length_mm: 300, width_mm: 200, height_mm: 100, weight_g: 500 });
    const [rates, setRates] = useState(null);
    const [rateId, setRateId] = useState(null);
    const [notify, setNotify] = useState(true);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!open) return;
        setRates(null); setRateId(null); setNotify(true);
        api.get('/shipping-settings', null, { skipGlobalErrorToast: true }).then((d) => {
            setSettings(d);
            const def = listOf(d?.packages).find((p) => p.default) || listOf(d?.packages)[0];
            setPkg(def ? def.id : 'custom');
        }).catch(() => { setSettings({}); setPkg('custom'); });
    }, [open, api]);
    const lineItems = items.map((i) => ({ order_item_id: i.id, quantity: i.quantity - (i.fulfilled_quantity || 0) }));
    const getRates = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/orders/${order.id}/label-rates`, { ...(pkg && pkg !== 'custom' ? { package_id: pkg } : { parcel }), items: lineItems }, { timeout: 60000 });
            setRates(listOf(r?.rates));
            setRateId(listOf(r?.rates)[0]?.id || null);
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    const buy = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/orders/${order.id}/labels`, { rate_id: rateId, items: lineItems, notify }, { timeout: 60000 });
            const f = listOf(r?.fulfillments).slice(-1)[0];
            const url = f?.label?.url || r?.label?.url;
            message.success(t('Label purchased'));
            if (url) window.open(url, '_blank', 'noopener');
            onDone();
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    const provider = settings?.labels?.provider;
    const pkgs = listOf(settings?.packages);
    const dim = (k, label) => <InputNumber min={1} addonAfter={k === 'weight_g' ? 'g' : 'mm'} placeholder={label} value={parcel[k]} onChange={(v) => setParcel((p) => ({ ...p, [k]: v || 0 }))} style={{ width: '100%' }} />;
    return (
        <Modal open={open} onCancel={onClose} title={t('Buy shipping label')} destroyOnHidden width={620}
            onOk={rates ? buy : getRates} okText={rates ? t('Buy label') : t('Get rates')} confirmLoading={busy} okButtonProps={{ disabled: (settings && !provider) || (rates && !rateId) }}>
            {!settings ? <Spin /> : !provider ? (
                <Alert type="info" showIcon message={t('Connect a label provider in Settings → Shipping first.')} />
            ) : (
                <>
                    <Form layout="vertical">
                        <Form.Item label={t('Package')}>
                            <Select value={pkg} onChange={(v) => { setPkg(v); setRates(null); }}
                                options={[...pkgs.map((p) => ({ value: p.id, label: `${p.name} · ${p.length_mm}×${p.width_mm}×${p.height_mm} mm` })), { value: 'custom', label: t('Custom size') }]} />
                        </Form.Item>
                        {pkg === 'custom' && (
                            <Row gutter={8}>
                                <Col span={6}>{dim('length_mm', t('Length'))}</Col>
                                <Col span={6}>{dim('width_mm', t('Width'))}</Col>
                                <Col span={6}>{dim('height_mm', t('Height'))}</Col>
                                <Col span={6}>{dim('weight_g', t('Weight', { context: 'shop' }))}</Col>
                            </Row>
                        )}
                    </Form>
                    {rates && (rates.length ? (
                        <Radio.Group value={rateId} onChange={(e) => setRateId(e.target.value)} style={{ width: '100%', marginTop: 12 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {rates.map((r) => (
                                    <Radio key={r.id} value={r.id} style={{ width: '100%' }}>
                                        <Space>
                                            <Text strong>{money(r.amount, r.currency)}</Text>
                                            <Text>{r.carrier} · {r.service}</Text>
                                            {(r.min_days || r.max_days) && <Text type="secondary">{t('{{min}}–{{max}} days', { min: r.min_days ?? r.max_days, max: r.max_days ?? r.min_days })}</Text>}
                                        </Space>
                                    </Radio>
                                ))}
                            </Space>
                        </Radio.Group>
                    ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No rates for this package')} />)}
                    {rates && rates.length > 0 && <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ marginTop: 12 }}>{t('Send shipping confirmation to the customer')}</Checkbox>}
                    {settings?.labels?.test_mode && <Alert type="warning" showIcon style={{ marginTop: 12 }} message={t('Test key: labels are not real.')} />}
                </>
            )}
        </Modal>
    );
};

const RefundModal = ({ order, open, onClose, onDone, preset }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const left = Math.max(0, (order?.total || 0) - (order?.refunded_total || 0));
    const [amount, setAmount] = useState(left);
    const [reason, setReason] = useState('');
    const [restock, setRestock] = useState(true);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!open) return;
        setAmount(preset?.amount ? Math.min(left, preset.amount) : left); setReason(preset?.reason || ''); setRestock(preset ? !!preset.restock : true);
    }, [open, left, preset]);
    const submit = async () => {
        if (!amount || amount > left) { message.error(t('Enter an amount up to {{max}}', { max: money(left, order.currency) })); return; }
        setBusy(true);
        try {
            const lines = restock ? listOf(order.items).map((i) => ({ order_item_id: i.id, quantity: i.quantity })) : [];
            await api.post(`/orders/${order.id}/refund`, { amount, reason, restock: lines, notify: true });
            message.success(t('Refund submitted'));
            onDone();
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} onCancel={onClose} title={t('Refund')} onOk={submit} confirmLoading={busy} okText={t('Refund {{amount}}', { amount: money(amount || 0, order?.currency) })} okButtonProps={{ danger: true }} destroyOnHidden>
            <Form layout="vertical">
                <Form.Item label={t('Amount')} extra={t('Available to refund: {{amount}}', { amount: money(left, order?.currency) })}>
                    <MoneyInput currency={order?.currency} value={amount} onChange={setAmount} />
                </Form.Item>
                <Form.Item label={t('Reason')}><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} /></Form.Item>
                <Checkbox checked={restock} onChange={(e) => setRestock(e.target.checked)}>{t('Restock items')}</Checkbox>
            </Form>
        </Modal>
    );
};

/** Merchant-created return (POST orders/{oid}/returns): quantities limited by order.returnable {item id: qty}. */
const ReturnModal = ({ order, open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const avail = order?.returnable || {};
    const items = useMemo(() => listOf(order?.items).filter((i) => Number(avail[i.id]) > 0), [order]); // eslint-disable-line react-hooks/exhaustive-deps
    const [qty, setQty] = useState({});
    const [reason, setReason] = useState({});
    const [note, setNote] = useState('');
    const [approve, setApprove] = useState(true);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) { setQty({}); setReason({}); setNote(''); setApprove(true); } }, [open]);
    const chosen = items.filter((i) => (qty[i.id] || 0) > 0);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/orders/${order.id}/returns`, { items: chosen.map((i) => ({ order_item_id: i.id, quantity: qty[i.id], reason: reason[i.id] || '' })), note, approve });
            message.success(t('Return created'));
            onDone();
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} title={t('Create return')} onCancel={onClose} onOk={submit} okText={t('Create return')} confirmLoading={busy} okButtonProps={{ disabled: !chosen.length }} width={640} destroyOnClose>
            <Table size="small" rowKey="id" dataSource={items} pagination={false} columns={[
                { title: t('Product', { context: 'shop' }), key: 'p', render: (_, i) => <>{i.title}{i.variant_title && i.variant_title !== 'Default' && <div><Text type="secondary" style={{ fontSize: 12 }}>{i.variant_title}</Text></div>}</> },
                { title: t('Quantity'), key: 'q', width: 110, render: (_, i) => <InputNumber size="small" min={0} max={Number(avail[i.id])} value={qty[i.id] || 0} onChange={(v) => setQty({ ...qty, [i.id]: v || 0 })} addonAfter={`/${avail[i.id]}`} style={{ width: 100 }} /> },
                { title: t('Reason'), key: 'r', render: (_, i) => <Input size="small" maxLength={200} value={reason[i.id] || ''} onChange={(e) => setReason({ ...reason, [i.id]: e.target.value })} /> },
            ]} />
            <Input.TextArea style={{ marginTop: 12 }} placeholder={t('Note')} autoSize={{ minRows: 2 }} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
            <Checkbox style={{ marginTop: 8 }} checked={approve} onChange={(e) => setApprove(e.target.checked)}>{t('Approve now')}</Checkbox>
        </Modal>
    );
};

const OrderDetail = () => {
    const { t } = useTranslation();
    const { oid } = useParams();
    const navigate = useNavigate();
    const { api, siteId, shop } = useSite();
    const [o, setO] = useState(null);
    const [err, setErr] = useState(null);
    const [modal, setModal] = useState(null);
    const [refundPreset, setRefundPreset] = useState(null);
    const [note, setNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    const load = async () => {
        try {
            const d = await api.get(`/orders/${oid}`);
            const ord = d?.order ? { ...d.order, ...d } : d;
            setO(ord);
            setNote(ord?.staff_note || '');
        } catch (e) { setErr(e); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [api, oid]);

    if (err) return <Result status={err.status === 404 ? '404' : 'error'} title={err.status === 404 ? t('Order not found') : (err.error || t('Failed to load'))} extra={<Button onClick={() => navigate(sitePath(siteId, 'orders'))}>{t('Orders')}</Button>} />;
    if (!o) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;

    const cur = o.currency;
    const items = listOf(o.items);
    const unfulfilled = items.some((i) => i.requires_shipping !== false && i.quantity > (i.fulfilled_quantity || 0));
    const cancelled = o.status === 'cancelled';
    const paid = ['paid', 'partially_refunded'].includes(o.financial_status);
    const refundable = paid && (o.total || 0) > (o.refunded_total || 0);
    const flags = o.flags || {};
    const canReturn = Object.values(o.returnable || {}).some((q) => Number(q) > 0);
    const canPickup = o.is_pickup && !o.pickup_ready_at && !cancelled && o.fulfillment_status !== 'fulfilled';
    const resendConfirmation = async () => {
        try { await api.post(`/orders/${o.id}/resend-confirmation`); message.success(t('Confirmation sent')); } catch (e) { toastErr(e); }
    };
    const readyForPickup = async () => {
        try { await api.post(`/orders/${o.id}/ready-for-pickup`, { notify: true }); message.success(t('Customer notified')); load(); } catch (e) { toastErr(e); }
    };

    const cancel = () => {
        let reason = '';
        let restock = true;
        let refund = paid;
        Modal.confirm({
            title: t('Cancel order {{name}}?', { name: orderName(o, shop) }),
            okText: t('Cancel order'),
            okButtonProps: { danger: true },
            cancelText: t('Keep order'),
            content: (
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                    <Input placeholder={t('Reason')} onChange={(e) => { reason = e.target.value; }} />
                    <Checkbox defaultChecked onChange={(e) => { restock = e.target.checked; }}>{t('Restock items')}</Checkbox>
                    {paid && <Checkbox defaultChecked onChange={(e) => { refund = e.target.checked; }}>{t('Refund the payment')}</Checkbox>}
                </Space>
            ),
            onOk: async () => {
                try { await api.post(`/orders/${o.id}/cancel`, { reason, restock, refund }); message.success(t('Order cancelled')); load(); } catch (e) { toastErr(e); throw e; }
            },
        });
    };
    const closeOrder = async () => {
        try { await api.post(`/orders/${o.id}/close`, { closed: o.status !== 'closed' }); message.success(t('Updated')); load(); } catch (e) { toastErr(e); }
    };
    const saveNote = async () => {
        setSavingNote(true);
        try { await api.post(`/orders/${o.id}/note`, { note }); message.success(t('Saved')); setO((x) => ({ ...x, staff_note: note })); } catch (e) { toastErr(e); } finally { setSavingNote(false); }
    };

    // timeline: backend timeline[] (oldest first) when present, else derived from the order
    const TL = {
        created: () => t('Order placed'),
        paid: (e) => t('Payment of {{amount}} received', { amount: money(e.amount ?? o.total, e.currency || cur) }),
        fulfilled: (e) => `${t('Items fulfilled')}${e.text ? ` · ${String(e.text).replace(/^Fulfilled\s*/, '')}` : ''}`,
        refund: (e) => `${t('Refunded {{amount}}', { amount: money(e.amount, e.currency || cur) })}${String(e.text || '').includes(':') ? ` · ${String(e.text).split(':').slice(1).join(':').trim()}` : ''}`,
        cancelled: () => t('Order cancelled'),
    };
    const events = listOf(o.timeline).length
        ? [...listOf(o.timeline)].reverse().map((e) => ({
            at: e.time, color: e.type === 'tracking' ? 'blue' : e.type === 'cancelled' ? 'red' : undefined,
            text: e.type === 'tracking' ? <Space size={4} wrap><TrackingTag value={e.status} />{e.text}</Space>
                : e.type === 'return' ? <Space size={4}>{t('Return')} <ReturnTag value={String(e.text || '').replace(/^Return\s*/, '')} /></Space>
                    : (TL[e.type] ? TL[e.type](e) : e.text),
        }))
        : [
            { at: o.created_at, text: t('Order placed') },
            o.paid_at && { at: o.paid_at, text: t('Payment of {{amount}} received', { amount: money(o.total, cur) }) },
            ...listOf(o.fulfillments).map((f) => ({ at: f.created_at, text: `${t('Items fulfilled')}${f.tracking_number ? ` · ${f.tracking_company || ''} ${f.tracking_number}` : ''}` })),
            ...listOf(o.refunds).map((r) => ({ at: r.created_at, text: `${t('Refunded {{amount}}', { amount: money(r.amount, cur) })}${r.reason ? ` · ${r.reason}` : ''}` })),
            o.cancelled_at && { at: o.cancelled_at, text: t('Order cancelled') },
            o.closed_at && { at: o.closed_at, text: t('Order closed') },
        ].filter(Boolean).sort((a, b) => String(b.at).localeCompare(String(a.at)));
    const syncTracking = async (f) => {
        try { const d = await api.post(`/orders/${o.id}/fulfillments/${f.id}/sync`); if (d?.id) setO((x) => ({ ...x, ...d })); else load(); message.success(t('Updated')); } catch (e) { toastErr(e); }
    };

    const customer = o.customer || (o.customer_id ? { id: o.customer_id } : null);
    const ship = o.shipping_line || {};

    return (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(sitePath(siteId, 'orders'))} />
                <Text strong style={{ fontSize: 18 }}>{orderName(o, shop)}</Text>
                <StatusTag value={o.financial_status} />
                <StatusTag value={o.fulfillment_status} />
                {o.status !== 'open' && <StatusTag value={o.status} />}
                {o.pickup_ready_at && <Tooltip title={fmtDate(o.pickup_ready_at)}><Tag color="green">{t('Ready for pickup')}</Tag></Tooltip>}
                <Text type="secondary">{fmtDate(o.created_at)}</Text>
                <div style={{ flex: 1 }} />
                <Dropdown menu={{
                    items: [{ key: 'packing-slip', label: t('Packing slip') }, { key: 'invoice', label: t('Invoice') }],
                    onClick: ({ key }) => openDocument(siteId, `/orders/${o.id}/${key}`),
                }}>
                    <Button icon={<PrinterOutlined />}>{t('Print')}</Button>
                </Dropdown>
                {!cancelled && unfulfilled && paid && <Button icon={<TagOutlined />} onClick={() => setModal('label')}>{t('Buy label')}</Button>}
                {canPickup && <Button onClick={readyForPickup}>{t('Ready for pickup')}</Button>}
                {!cancelled && unfulfilled && <Button type="primary" onClick={() => setModal('fulfill')}>{t('Fulfill items')}</Button>}
                {canReturn && <Button onClick={() => setModal('return')}>{t('Create return')}</Button>}
                {refundable && <Button onClick={() => setModal('refund')}>{t('Refund')}</Button>}
                {!cancelled && (
                    <Dropdown menu={{
                        items: [
                            ...(o.number ? [{ key: 'resend', label: t('Resend confirmation') }] : []),
                            { key: 'close', label: o.status === 'closed' ? t('Reopen order') : t('Close order') },
                            { type: 'divider' },
                            { key: 'cancel', danger: true, label: t('Cancel order') },
                        ],
                        onClick: ({ key }) => (key === 'cancel' ? cancel() : key === 'resend' ? resendConfirmation() : closeOrder()),
                    }}>
                        <Button icon={<MoreOutlined />} />
                    </Dropdown>
                )}
            </Space>
            {flags.oversold?.length > 0 && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('Some items were oversold — check stock before fulfilling.')} />}
            {listOf(o.payment_alerts).map((a, i) => (
                <Alert key={`pa${i}`} type="error" showIcon style={{ marginBottom: 12 }}
                    message={a.type === 'duplicate_payment' ? t('Paid twice — refund the extra payment') : t('Paid after it was cancelled — refund or reopen')}
                    description={<Text type="secondary" style={{ fontSize: 12 }}>{money(a.amount, cur)}{a.payment_ref ? ` · ${a.payment_ref}` : ''}{a.at ? ` · ${fmtDate(a.at)}` : ''}</Text>}
                    action={refundable && (
                        <Button size="small" danger onClick={() => { setRefundPreset({ amount: a.amount, restock: a.type === 'paid_after_cancel', reason: a.type === 'duplicate_payment' ? t('Duplicate payment') : t('Paid after cancellation') }); setModal('refund'); }}>
                            {t('Refund')}
                        </Button>
                    )} />
            ))}
            {flags.amount_mismatch && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={t('The amount paid does not match the order total.')} />}
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Card title={t('Items')} style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
                        <Table size="small" rowKey="id" dataSource={items} pagination={false} scroll={{ x: 560 }} columns={[
                            {
                                title: t('Product', { context: 'shop' }), key: 'p', render: (_, i) => (
                                    <Space>
                                        <Thumb src={i.image?.src} size={40} />
                                        <div>
                                            {i.product_id ? <Link to={sitePath(siteId, `products/${i.product_id}`)}>{i.title}</Link> : i.title}
                                            {i.variant_title && i.variant_title !== 'Default' && <div><Text type="secondary" style={{ fontSize: 12 }}>{i.variant_title}</Text></div>}
                                            {i.sku && <div><Text type="secondary" style={{ fontSize: 12 }}>SKU: {i.sku}</Text></div>}
                                        </div>
                                    </Space>
                                ),
                            },
                            { title: t('Price'), key: 'pr', width: 130, render: (_, i) => `${money(i.price, cur)} × ${i.quantity}` },
                            { title: t('Fulfilled'), key: 'f', width: 90, render: (_, i) => (i.requires_shipping === false ? '-' : `${i.fulfilled_quantity || 0}/${i.quantity}`) },
                            { title: t('Total', { context: 'shop' }), key: 't', width: 110, align: 'right', render: (_, i) => money(i.line_total ?? i.price * i.quantity, cur) },
                        ]} />
                    </Card>

                    <Card title={t('Payment')} style={{ marginBottom: 16 }} extra={<StatusTag value={o.financial_status} />}>
                        <Descriptions column={1} size="small" labelStyle={{ width: 180 }} contentStyle={{ justifyContent: 'flex-end' }}>
                            <Descriptions.Item label={t('Subtotal')}>{money(o.subtotal, cur)}</Descriptions.Item>
                            {(o.discount_total > 0 || listOf(o.discount_codes).length > 0) && (
                                <Descriptions.Item label={`${t('Discount', { context: 'shop' })} ${listOf(o.discount_codes).map((d) => d.code).join(', ')}`}>-{money(o.discount_total, cur)}</Descriptions.Item>
                            )}
                            <Descriptions.Item label={`${t('Shipping')}${ship.name ? ` (${ship.name})` : ''}`}>{money(o.shipping_total, cur)}</Descriptions.Item>
                            <Descriptions.Item label={o.taxes_included ? t('Tax (included)') : t('Tax')}>{money(o.tax_total, cur)}</Descriptions.Item>
                            <Descriptions.Item label={<Text strong>{t('Total', { context: 'shop' })}</Text>}><Text strong>{money(o.total, cur)}</Text>{o.base_currency && o.base_currency !== cur && o.base_total != null ? <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>≈ {money(o.base_total, o.base_currency)}</Text> : null}</Descriptions.Item>
                            {o.refunded_total > 0 && <Descriptions.Item label={t('Refunded')}><Text type="danger">-{money(o.refunded_total, cur)}</Text></Descriptions.Item>}
                            {o.refunded_total > 0 && <Descriptions.Item label={t('Net payment')}>{money(o.total - o.refunded_total, cur)}</Descriptions.Item>}
                        </Descriptions>
                        {o.payment_ref && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {(o.payment_provider || 'stripe').toUpperCase()}{o.payment_mode === 'test' ? ` (${t('test mode')})` : ''}: <Text copyable style={{ fontSize: 12 }}>{o.payment_ref}</Text>
                            </Text>
                        )}
                    </Card>

                    {listOf(o.fulfillments).length > 0 && (
                        <Card title={t('Fulfillments')} style={{ marginBottom: 16 }} styles={{ body: { paddingTop: 8 } }}>
                            {listOf(o.fulfillments).map((f, idx) => (
                                <div key={f.id} style={{ padding: '8px 0', borderTop: idx ? '1px solid var(--ant-color-border-secondary, #f0f0f0)' : undefined }}>
                                    <Space wrap size={6}>
                                        <TrackingTag value={f.status} />
                                        <Text strong>{f.carrier_name || f.tracking_company || t('Shipment')}</Text>
                                        {f.tracking_number && (f.tracking_url ? <a href={f.tracking_url} target="_blank" rel="noopener noreferrer">{f.tracking_number}</a> : <Text copyable>{f.tracking_number}</Text>)}
                                        <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(f.created_at)}</Text>
                                        {f.estimated_delivery && f.status !== 'delivered' && <Text type="secondary" style={{ fontSize: 12 }}>{t('Expected {{date}}', { date: fmtDate(f.estimated_delivery, false) })}</Text>}
                                        {f.delivered_at && <Text type="secondary" style={{ fontSize: 12 }}>{t('Delivered {{date}}', { date: fmtDate(f.delivered_at) })}</Text>}
                                        {f.label?.url && <a href={f.label.url} target="_blank" rel="noopener noreferrer"><TagOutlined /> {t('Label')}</a>}
                                        {f.tracking_number && <Tooltip title={t('Refresh tracking')}><Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => syncTracking(f)} /></Tooltip>}
                                    </Space>
                                    {listOf(f.items).length > 0 && <div><Text type="secondary" style={{ fontSize: 12 }}>{listOf(f.items).map((fi) => { const it = items.find((x) => x.id === fi.order_item_id); return `${it?.title || '#'}${fi.quantity > 1 ? ` × ${fi.quantity}` : ''}`; }).join(', ')}</Text></div>}
                                    {f.tracking_error && <div><Text type="warning" style={{ fontSize: 12 }}>{f.tracking_error}</Text></div>}
                                    {listOf(f.events).length > 0 && (
                                        <Collapse ghost size="small" items={[{
                                            key: 'e', label: <Text type="secondary" style={{ fontSize: 12 }}>{t('{{n}} tracking events', { n: listOf(f.events).length })}</Text>,
                                            children: <Timeline style={{ marginTop: 8 }} items={listOf(f.events).map((e) => ({ children: <div><Space size={4} wrap><TrackingTag value={e.status} />{e.description}{e.location && <Text type="secondary">· {e.location}</Text>}</Space><div><Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(e.time)}</Text></div></div> }))} />,
                                        }]} />
                                    )}
                                </div>
                            ))}
                        </Card>
                    )}

                    {listOf(o.returns).length > 0 && (
                        <Card title={t('Returns')} style={{ marginBottom: 16 }} extra={<Link to={sitePath(siteId, 'returns')}>{t('All returns')}</Link>}>
                            {listOf(o.returns).map((r) => (
                                <div key={r.id} style={{ marginBottom: 6 }}>
                                    <Space wrap size={6}>
                                        <ReturnTag value={r.status} />
                                        <Text>{listOf(r.items).map((ri) => { const it = items.find((x) => x.id === ri.order_item_id); return `${it?.title || '#'} × ${ri.quantity}${ri.reason ? ` (${ri.reason})` : ''}`; }).join(', ')}</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(r.created_at)}</Text>
                                    </Space>
                                    {r.customer_note && <div><Text type="secondary" style={{ fontSize: 12 }}>{r.customer_note}</Text></div>}
                                </div>
                            ))}
                        </Card>
                    )}

                    <Card title={t('Timeline')} style={{ marginBottom: 16 }}>
                        <Timeline items={events.map((e) => ({ color: e.color, children: <div><div>{e.text}</div><Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(e.at)}</Text></div> }))} />
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title={t('Customer')} size="small" style={{ marginBottom: 16 }}>
                        {customer?.id ? <Link to={sitePath(siteId, `customers/${customer.id}`)}>{customer.name || o.shipping_address?.name || o.email}</Link> : <Text>{o.shipping_address?.name || t('Guest')}</Text>}
                        {customer?.orders_count > 0 && <div><Text type="secondary">{t('{{n}} orders', { n: customer.orders_count })}</Text></div>}
                        <Divider style={{ margin: '10px 0' }} />
                        <Text type="secondary">{t('Contact')}</Text>
                        <div><a href={`mailto:${o.email}`}>{o.email}</a></div>
                        {o.phone && <div>{o.phone}</div>}
                        <Divider style={{ margin: '10px 0' }} />
                        <Text type="secondary">{t('Shipping address')}</Text>
                        <Address a={o.shipping_address} />
                        {ship.name && <div style={{ marginTop: 6 }}><Text type="secondary">{ship.name}{ship.eta ? ` · ${ship.eta}` : ''}</Text></div>}
                        <Divider style={{ margin: '10px 0' }} />
                        <Text type="secondary">{t('Billing address')}</Text>
                        {o.billing_address && Object.keys(o.billing_address).length ? <Address a={o.billing_address} /> : <div><Text type="secondary">{t('Same as shipping address')}</Text></div>}
                    </Card>
                    {o.note && (
                        <Card title={t('Customer note')} size="small" style={{ marginBottom: 16 }}>
                            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{o.note}</Paragraph>
                        </Card>
                    )}
                    <ShopOrderExtras order={o} onChanged={load} />
                    <CustomFieldsCard owner="order" oid={o.id} />
                    <Card title={t('Staff note')} size="small" style={{ marginBottom: 16 }}>
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} autoSize={{ minRows: 3 }} placeholder={t('Only visible to staff')} />
                        {note !== (o.staff_note || '') && <Button size="small" type="primary" style={{ marginTop: 8 }} loading={savingNote} onClick={saveNote}>{t('Save')}</Button>}
                    </Card>
                </Col>
            </Row>
            <FulfillModal order={o} open={modal === 'fulfill'} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
            <LabelModal order={o} open={modal === 'label'} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
            <ReturnModal order={o} open={modal === 'return'} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
            <RefundModal order={o} open={modal === 'refund'} preset={refundPreset} onClose={() => { setModal(null); setRefundPreset(null); }} onDone={() => { setModal(null); setRefundPreset(null); load(); }} />
        </div>
    );
};

export default OrderDetail;
